import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip, IconButton,
  Stack,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import BackButton from '../../components/BackButton/BackButton';
import { useNavigate, useParams } from 'react-router';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import {fetchFair, publishFair, pushFloorPlans, updateFair} from '../../api/fairs';
import { createToastNotification } from '../../utilities/helpers';
import dayjs, { Dayjs } from 'dayjs';
import { IFairDetails } from '../../common/interfaces/fairs';
import { FloorModal } from '../FloorDrawing/FloorModal';
import ConfirmationModal from '../../components/ConfirmationModal';
import { GridColDef} from "@mui/x-data-grid";
import Tooltip from "@mui/material/Tooltip";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import {FairFloorPlanRow} from "./FairHalls/FairFloorPlanRow";
import PublishIcon from '@mui/icons-material/Publish';
import CancelIcon from '@mui/icons-material/Cancel';

const FairDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isEdit, setIsEdit] = useState(false);
  const [fair, setFair] = useState<IFairDetails>({
    id: 0,
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    category: [],
    status: '',
    floorPlans: [],
  });
  const [categoryInput, setCategoryInput] = useState('');
  const [fetched, setFetched] = useState(false);
  const [selectedFloorPlan, setSelectedFloorPlan] = useState<any>(null);
  const [modalOpened, setModalOpened] = useState(false);
  const [pushFloorPlansModalOpened, setPushFloorPlansModalOpened] = useState(false);
  const [hallsAndFloorPlans, setHallsAndFloorPlans] = useState([]);
  const [
    selectedHallIDsForFloorPlanPublish,
    setSelectedHallIDsForFloorPlanPublish
  ] = useState<number[]>([]);

  const handleEditClick = () => {
    setIsEdit(true);
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleEditSubmit = async () => {
    if (!id || !fair) {
      createToastNotification('error', 'Podaci sajma nisu kompletni.');
      return;
    }
    try {
      const updatedFair = {
        ...fair,
        id: id,
        startDate: fair.startDate ? fair.startDate : null,
        endDate: fair.endDate ? fair.endDate : null,
      };
      await updateFair(updatedFair);
      createToastNotification('success', 'Sajam uspešno izmenjen.');
      setIsEdit(false);
    } catch (error) {
      createToastNotification('error', 'Greška pri izmeni sajma.');
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    setFair((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDateChange = (
    field: 'startDate' | 'endDate',
    date: Dayjs | null
  ) => {
    const formattedDate = date ? date.format('YYYY-MM-DD') : '';
    setFair((prev) => ({
      ...prev,
      [field]: formattedDate,
    }));
  };

  const fetchFairDetails = async () => {
    if (!id) {
      createToastNotification('error', 'Dohvatanje sajma nije uspelo');
      return;
    }
    try {
      const response = await fetchFair(Number(id));
      setFair({
        ...response.fair,
        startDate: response.fair.startDate
          ? dayjs(response.fair.startDate).format('YYYY-MM-DD')
          : null,
        endDate: response.fair.endDate
          ? dayjs(response.fair.endDate).format('YYYY-MM-DD')
          : null,
        category: response.fair.category || [],
      });
      let halls: any = [];
      for (let i = 0; i < response.fair?.floorPlans?.length; i++) {
        if (halls[response.fair?.floorPlans[i].hall.id]) {
          halls[response.fair?.floorPlans[i].hall.id].floorPlans = [...halls[response.fair?.floorPlans[i].hall.id].floorPlans, response.fair?.floorPlans[i]]
        } else {
          halls[response.fair?.floorPlans[i].hall.id] = {
            id: response.fair?.floorPlans[i].hall.id,
            name: response.fair?.floorPlans[i].hall.name,
            description: response.fair?.floorPlans[i].hall.description ?? '',
            floorPlans: [response.fair?.floorPlans[i]]
          }
        }
      }
      setFetched(true);
      setHallsAndFloorPlans(halls)
    } catch (error) {
      createToastNotification('error', 'Dohvatanje sajma nije uspelo!');
    }
  };

  useEffect(() => {
    if (!fetched) {
      fetchFairDetails();
    }
    // eslint-disable-next-line
  }, [fetched]);

  const handleAddChip = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && categoryInput.trim() !== '') {
      setFair((prev) => ({
        ...prev,
        category: [...prev.category, categoryInput.trim()],
      }));
      setCategoryInput('');
    }
  };

  const handleDeleteChip = (index: number) => {
    setFair((prev) => ({
      ...prev,
      category: prev.category.filter((_, i) => i !== index),
    }));
  };

  const handleHallSelectedEvent = (hallID: number, checked: boolean) => {

      let selected = selectedHallIDsForFloorPlanPublish;
      if (checked) {
          if (!selected.includes(hallID)) {
            selected = [...selected, hallID]
          }
      } else {
          selected = selected.filter((item) => item !== hallID)
      }

      setSelectedHallIDsForFloorPlanPublish(selected);
  }

  const [resetTrigger, setResetTrigger] = useState(false);

  return (
    <>
      <Stack direction="column" gap={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <BackButton title="SAJMOVI" backNavigate={handleBack} />
        </Stack>

        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h5">Detalji o sajmu</Typography>
              <Button variant="contained" onClick={handleEditClick}>
                <EditIcon />
              </Button>
            </Stack>
            <Stack p={4} spacing={4} mt={4} ml={4} mr={4}>
              <TextField
                fullWidth
                label="Naziv"
                value={fair?.name || ''}
                disabled={!isEdit}
                onChange={(e) => handleFieldChange('name', e.target.value)}
              />
              <TextField
                fullWidth
                label="Opis"
                multiline
                rows={3}
                value={fair?.description || ''}
                disabled={!isEdit}
                onChange={(e) =>
                  handleFieldChange('description', e.target.value)
                }
              />
              <Stack direction="row" gap={3}>
                <DatePicker
                  label={'Datum početka'}
                  disabled={!isEdit}
                  sx={{ flex: 1 }}
                  value={fair.startDate ? dayjs(fair.startDate) : null}
                  onChange={(date) => handleDateChange('startDate', date)}
                />
                <DatePicker
                  label={'Datum završetka'}
                  disabled={!isEdit}
                  sx={{ flex: 1 }}
                  value={fair.endDate ? dayjs(fair.endDate) : null}
                  onChange={(date) => handleDateChange('endDate', date)}
                />
              </Stack>
              <Autocomplete
                multiple
                limitTags={5}
                options={[]}
                disabled={!isEdit}
                value={fair.category}
                noOptionsText=""
                onChange={(event, newValue) => {
                  setFair((prev) => ({
                    ...prev,
                    category: newValue as string[],
                  }));
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const tagProps = getTagProps({ index });
                    return (
                      <Chip
                        label={option}
                        {...tagProps}
                        color="primary"
                        size="small"
                        onDelete={() => handleDeleteChip(index)}
                        sx={{
                          maxWidth: 150,
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                        }}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Kategorija"
                    variant="outlined"
                    fullWidth
                    value={categoryInput}
                    onChange={(e) => setCategoryInput(e.target.value)}
                    onKeyDown={handleAddChip}
                  />
                )}
              />
              {isEdit && (
                <Stack direction="row" justifyContent="flex-start">
                  <Button variant="contained" onClick={handleEditSubmit}>
                    Izmeni sajam
                  </Button>
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Box m={2}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="h5">Hale</Typography>
              {selectedHallIDsForFloorPlanPublish.length > 0 ?
                <Stack direction={"row"}>
                  <Tooltip title="Objavi prostorne planove za odabrane hale" arrow>
                    <IconButton
                        sx={{marginRight: 2}}
                        onClick={() => {
                          setPushFloorPlansModalOpened(true)
                        }}
                    >
                      <PublishIcon color="primary" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Otkaži objavu prostornih planova" arrow>
                    <IconButton
                        sx={{ marginRight: 5 }}
                        onClick={() => {
                          setSelectedHallIDsForFloorPlanPublish([])
                          setResetTrigger((prev) => !prev);
                        }}
                    >
                      <CancelIcon color="error" />
                    </IconButton>
                  </Tooltip>

                </Stack>:
                <Button
                    variant="contained"
                    startIcon={<AddIcon/>}
                    onClick={() => {
                      setSelectedFloorPlan(null);
                      setModalOpened(true);
                    }}
                >
                  Kreiraj novi prostorni plan
                </Button>
            }
          </Stack>
          <TableContainer component={Paper} sx={{marginTop:"15px"}}>
            <Table aria-label="collapsible table">
              <TableHead>
                <TableRow>
                  <TableCell align="left" sx={{width:"50px"}}/>
                  <TableCell align="left" sx={{width:"100px"}}>ID hale</TableCell>
                  <TableCell align="left" sx={{width:"300px"}}>Naziv</TableCell>
                  <TableCell align="left">Opis</TableCell>
                  <TableCell align="right"/>
                </TableRow>
              </TableHead>
              <TableBody>
                {hallsAndFloorPlans?.map((row:any) => (
                    <FairFloorPlanRow
                        key={row.id}
                        hallWithFloorPlans={row}
                        fetchFairDetails={fetchFairDetails}
                        setSelectedFloorPlan={setSelectedFloorPlan}
                        setOpenFloorPlanModal={setModalOpened}
                        handleHallSelectedEvent={handleHallSelectedEvent}
                        resetTrigger={resetTrigger}
                    />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Stack>
      <FloorModal
        open={modalOpened}
        setOpen={setModalOpened}
        floorPlan={selectedFloorPlan}
        setFloorPlan={setSelectedFloorPlan}
        fairID={fair.id}
        fairName={fair.name}
        fetchFairDetails={fetchFairDetails}
      />
      {pushFloorPlansModalOpened && (
          <ConfirmationModal
              confirmationModalTitle="Objavite prostorne planove"
              cancelAction={() => setPushFloorPlansModalOpened(false)}
              handleAction={async () => {
                if (selectedHallIDsForFloorPlanPublish.length <= 0) {
                  createToastNotification('error', 'Odaberite hale za objavu prostornih planova')
                  setPushFloorPlansModalOpened(false)
                  return;
                }
                try {
                  const response = await pushFloorPlans(fair.id, selectedHallIDsForFloorPlanPublish)
                  createToastNotification('success', response.message)
                } catch (error: any) {
                  createToastNotification('error', error.response?.data?.message)
                }
                setSelectedHallIDsForFloorPlanPublish([])
                setResetTrigger((prev) => !prev);
                setPushFloorPlansModalOpened(false)
              }
              }
              confirmBtnTitle="Objavi"
              modalDescription="Da li ste sigurni da želite da objavite prostorne planove?"
              cancelBtnTitle="Odustani"
          />
      )}
    </>
  );
};

export default FairDetails;
